import type {
  OrganizationAdminItem,
  OrganizationType
} from '@stratumn/studio-shared';
import { OrganizationTypeSchema } from '@stratumn/studio-shared';
import { BooleanToggleCell } from '@/components/env-var/BooleanToggleCell';
import {
  EnvVarCellInput,
  EnvVarCellText,
  EnvVarTable
} from '@/components/env-var/EnvVarTable';
import { ProcessTagBadge } from '@/components/process-tag-badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { ACCOUNT_UI_URL } from '@/utils/constants';

const NONE_VALUE = '__none__';
const ORGANIZATION_TYPES = OrganizationTypeSchema.options;

type Props = {
  organizations: OrganizationAdminItem[];
  mains: OrganizationAdminItem[];
  onChangePrimary: (rowId: string, primaryRowId: string | null) => void;
  onChangeType: (rowId: string, type: OrganizationType) => void;
  onToggleExternal: (rowId: string, value: boolean) => void;
  disabled?: boolean;
};

export const OrganizationsAdminTable = ({
  organizations,
  mains,
  onChangePrimary,
  onChangeType,
  onToggleExternal,
  disabled = false
}: Props) => (
  <TooltipProvider>
    <EnvVarTable
      headers={[
        { label: 'Organization' },
        { label: 'Scenario', className: 'w-0' },
        { label: 'Primary' },
        { label: 'Accepts external members', center: true }
      ]}
    >
      {organizations.map(org => {
        const selectableMains = mains.filter(main => main.rowId !== org.rowId);
        const isProd = org.organizationType === 'prod';
        const primarySelect = (
          <Select
            disabled={disabled || isProd}
            onValueChange={value =>
              onChangePrimary(org.rowId, value === NONE_VALUE ? null : value)
            }
            value={org.primaryOrganization?.rowId ?? NONE_VALUE}
          >
            <SelectTrigger
              aria-label="Primary organization"
              className="h-10 w-full rounded-none border-0 shadow-none focus:ring-0 disabled:hover:bg-background"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None (main)</SelectItem>
              {selectableMains.map(main => (
                <SelectItem key={main.rowId} value={main.rowId}>
                  {main.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
        return (
          <tr key={org.rowId}>
            <EnvVarCellText>
              <a
                className="-mx-3 -my-2.5 block px-3 py-2.5 hover:underline"
                href={`${ACCOUNT_UI_URL}/organization/${org.rowId}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {org.name}
              </a>
            </EnvVarCellText>
            <EnvVarCellInput>
              <Select
                disabled={disabled}
                onValueChange={value =>
                  onChangeType(org.rowId, OrganizationTypeSchema.parse(value))
                }
                value={org.organizationType}
              >
                <SelectTrigger
                  aria-label="Organization scenario"
                  className="h-10 w-full gap-2 rounded-none border-0 shadow-none focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORGANIZATION_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      <ProcessTagBadge tag={type} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EnvVarCellInput>
            <EnvVarCellInput>
              {isProd ? (
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <span className="block w-full">{primarySelect}</span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    A <ProcessTagBadge className="align-middle" tag="prod" />{' '}
                    organization can't be a sub-organization. Set its scenario
                    to dev, demo or test first.
                  </TooltipContent>
                </Tooltip>
              ) : (
                primarySelect
              )}
            </EnvVarCellInput>
            <EnvVarCellInput>
              <BooleanToggleCell
                disabled={disabled}
                falseLabel="No"
                onChange={value => onToggleExternal(org.rowId, value)}
                trueLabel="Yes"
                value={org.acceptsExternalMembers}
              />
            </EnvVarCellInput>
          </tr>
        );
      })}
    </EnvVarTable>
  </TooltipProvider>
);
